import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  FormHelperText,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { AdminUser } from '@/types'
import { useChangeAdminPassword } from '@/hooks/useAdminData'
import { useToast } from '@/hooks/useToast'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'
import { validatePasswordStrength } from '@shared/passwordStrength'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'
import { resolveApiErrorMessage } from '@/services/admin/baseService'

const passwordConstraint = TEXT_CONSTRAINTS['user.password']

const passwordSchema = z
  .object({
    new_password: z
      .string()
      .min(
        passwordConstraint.minLength ?? 8,
        `Password must be at least ${passwordConstraint.minLength ?? 8} characters`
      )
      .max(
        passwordConstraint.maxLength ?? 128,
        `Password must be at most ${passwordConstraint.maxLength ?? 128} characters`
      )
      .superRefine((password, context) => {
        const strength = validatePasswordStrength(password)
        if (!strength.valid) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: strength.errors[0] ?? 'Password does not meet complexity requirements',
          })
        }
      }),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  })

type PasswordFormData = z.infer<typeof passwordSchema>

interface PasswordChangeModalProps {
  isOpen: boolean
  onClose: () => void
  user: AdminUser | null
}

export function PasswordChangeModal({ isOpen, onClose, user }: PasswordChangeModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const changePasswordMutation = useChangeAdminPassword()
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      new_password: '',
      confirm_password: '',
    },
  })

  const onSubmit = async (data: PasswordFormData) => {
    if (!user) return
    
    setIsSubmitting(true)
    
    try {
      const newPasswordCheck = validateTextWithConstraint(
        data.new_password,
        passwordConstraint,
        { requiredOverride: true }
      )

      if (!newPasswordCheck.valid || !newPasswordCheck.value) {
        toast.error({
          titleKey: 'adminUsers.passwordChangeError',
          descriptionKey: 'adminUsers.form.passwordValidationFailed'
        })
        setIsSubmitting(false)
        return
      }

      await changePasswordMutation.mutateAsync({
        userId: user.id,
        data: {
          new_password: newPasswordCheck.value,
        },
      })
      
      toast.success({
        titleKey: 'adminUsers.passwordChangeSuccess',
        descriptionKey: 'adminUsers.passwordChangeSuccessOther',
        descriptionParams: { username: user.username }
      })
      
      onClose()
      reset()
    } catch (error: unknown) {
      toast.error({
        titleKey: 'adminUsers.passwordChangeError',
        description: resolveApiErrorMessage(error, t('common.unknownError')),
        duration: 6000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('adminUsers.changePasswordTitle')}
        </ModalHeader>
        <ModalCloseButton />
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  {t('adminUsers.changingUserPassword', { username: user.username })}
                </AlertDescription>
              </Alert>

              <FormControl isInvalid={!!errors.new_password} isRequired>
                <FormLabel>{t('adminUsers.form.newPassword')}</FormLabel>
                <Input
                  type="password"
                  placeholder={t('adminUsers.form.newPasswordPlaceholder')}
                  maxLength={passwordConstraint.maxLength}
                  {...register('new_password')}
                />
                <FormHelperText>{t('adminUsers.form.passwordHelp')}</FormHelperText>
                <FormErrorMessage>{errors.new_password?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.confirm_password} isRequired>
                <FormLabel>{t('adminUsers.form.confirmPassword')}</FormLabel>
                <Input
                  type="password"
                  placeholder={t('adminUsers.form.confirmPasswordPlaceholder')}
                  maxLength={passwordConstraint.maxLength}
                  {...register('confirm_password')}
                />
                <FormErrorMessage>{errors.confirm_password?.message}</FormErrorMessage>
              </FormControl>
              
              <Alert status="warning" size="sm">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('adminUsers.passwordChangeWarning')}
                </Text>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
            >
              {t('adminUsers.changePassword')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
